import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import ImageUpload from '../../components/ImageUpload';
import MultiImageUpload from '../../components/MultiImageUpload';
import { useAuth } from '../../lib/auth';
import API from '../../lib/api';
import { toast } from 'sonner';

const BADGE_OPTIONS = ['Featured', 'Popular', 'New', 'Luxury', 'Family'];
const inputCls = 'bg-[#232323] border-white/[0.10] rounded-xl h-11 text-sm text-white placeholder:text-white/25 focus:border-gold focus:ring-1 focus:ring-gold/20';
const labelCls = 'text-[10px] uppercase tracking-widest text-white/35 mb-1.5 block font-medium';

export default function AdminListingForm() {
  const { id } = useParams();
  const { isAdmin, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState([]);
  const [newAmenity, setNewAmenity] = useState('');
  const [form, setForm] = useState({
    location_id: '', title: '', subtitle: '', short_description: '',
    cover_image: '', gallery_images: [], amenities: [],
    guest_capacity: 2, bedrooms: 1, bathrooms: 1, beds: 1,
    badges: [], display_order: 0, is_visible: true,
    hostaway_booking_url: '', cta_text: 'Book Now'
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAdmin) { navigate('/login'); return; }
    API.get('/admin/locations').then(r => { setLocations(r.data.locations); if (!isEdit) setLoading(false); });
    if (isEdit) { API.get(`/admin/listings/${id}`).then(r => { setForm(r.data); setLoading(false); }).catch(() => { toast.error('Listing not found'); navigate('/admin/listings'); }); }
  }, [id, isAdmin, navigate, isEdit, authLoading]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const addAmenity = () => { if (newAmenity.trim() && !form.amenities.includes(newAmenity.trim())) { set('amenities', [...form.amenities, newAmenity.trim()]); setNewAmenity(''); } };
  const removeAmenity = (a) => set('amenities', form.amenities.filter(x => x !== a));
  const toggleBadge = (b) => set('badges', form.badges.includes(b) ? form.badges.filter(x => x !== b) : [...form.badges, b]);
  const addGalleryImage = (url) => set('gallery_images', [...form.gallery_images, url]);
  const removeGalleryImage = (i) => set('gallery_images', form.gallery_images.filter((_, idx) => idx !== i));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    if (!form.location_id) { toast.error('Please select a location'); return; }
    if (!form.hostaway_booking_url) { toast.error('Hostaway Booking URL is required'); return; }
    setSaving(true);
    try {
      if (isEdit) { await API.put(`/admin/listings/${id}`, form); toast.success('Listing updated'); }
      else { await API.post('/admin/listings', form); toast.success('Listing created'); }
      navigate('/admin/listings');
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  if (loading) return <div className="min-h-screen pt-20 flex items-center justify-center"><div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen pt-20 bg-[#101010]" data-testid="admin-listing-form">
      <div className="bg-[#161616] border-b border-white/[0.06] py-8 px-4 sm:px-8">
        <div className="max-w-4xl mx-auto">
          <Link to="/admin/listings" className="inline-flex items-center gap-1.5 text-white/30 hover:text-gold text-xs uppercase tracking-widest mb-3 transition-colors"><ArrowLeft className="w-3.5 h-3.5" /> Back to Listings</Link>
          <h1 className="font-heading text-2xl text-white font-bold">{isEdit ? 'Edit Listing' : 'New Listing'}</h1>
        </div>
      </div>
      <div className="max-w-4xl mx-auto px-4 sm:px-8 py-8">
        <form onSubmit={handleSubmit} className="space-y-8">
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Basic Information</h2>
            <div><label className={labelCls}>Location *</label>
              <select value={form.location_id} onChange={e => set('location_id', e.target.value)} required
                className="w-full bg-[#232323] border border-white/[0.10] rounded-xl h-11 px-3 text-sm text-white focus:outline-none focus:border-gold" data-testid="listing-location">
                <option value="">Select a location...</option>
                {locations.map(loc => <option key={loc.id} value={loc.id}>{loc.name}</option>)}
              </select></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Title *</label><Input value={form.title} onChange={e => set('title', e.target.value)} required placeholder="e.g. The Yorkville Suite" className={inputCls} data-testid="listing-title" /></div>
              <div><label className={labelCls}>Subtitle</label><Input value={form.subtitle} onChange={e => set('subtitle', e.target.value)} placeholder="e.g. Premium Downtown Living" className={inputCls} data-testid="listing-subtitle" /></div>
            </div>
            <div><label className={labelCls}>Short Description</label>
              <textarea value={form.short_description} onChange={e => set('short_description', e.target.value)} rows={3} placeholder="Brief description for listing cards"
                className="w-full bg-[#232323] border border-white/[0.10] rounded-xl p-4 text-sm text-white resize-none focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold/20 placeholder:text-white/25" data-testid="listing-description" /></div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.03 }} className="bg-[#1c1c1c] border-2 border-gold/20 rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Hostaway Booking Link</h2>
            <p className="text-xs text-white/35">When visitors click this listing, they will be redirected to this URL to complete their booking.</p>
            <div><label className={labelCls}>Hostaway Booking URL *</label><Input value={form.hostaway_booking_url} onChange={e => set('hostaway_booking_url', e.target.value)} required placeholder="https://app.hostaway.com/..." className={inputCls} data-testid="listing-booking-url" /></div>
            <div><label className={labelCls}>CTA Button Text</label><Input value={form.cta_text} onChange={e => set('cta_text', e.target.value)} placeholder="e.g. Book Now" className={`${inputCls} max-w-xs`} data-testid="listing-cta-text" /></div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.06 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Images</h2>
            <ImageUpload value={form.cover_image} onChange={v => set('cover_image', v)} label="Cover Image (Main listing photo)" />
            <MultiImageUpload
              images={form.gallery_images}
              onChange={v => set('gallery_images', v)}
              label="Gallery Images (select multiple)"
            />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.09 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Property Details</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><label className={labelCls}>Guests</label><Input type="number" min={1} value={form.guest_capacity} onChange={e => set('guest_capacity', parseInt(e.target.value) || 1)} className={inputCls} data-testid="listing-guests" /></div>
              <div><label className={labelCls}>Bedrooms</label><Input type="number" min={0} value={form.bedrooms} onChange={e => set('bedrooms', parseInt(e.target.value) || 0)} className={inputCls} /></div>
              <div><label className={labelCls}>Bathrooms</label><Input type="number" min={0} value={form.bathrooms} onChange={e => set('bathrooms', parseInt(e.target.value) || 0)} className={inputCls} /></div>
              <div><label className={labelCls}>Beds</label><Input type="number" min={0} value={form.beds} onChange={e => set('beds', parseInt(e.target.value) || 0)} className={inputCls} /></div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Amenities</h2>
            <div className="flex flex-wrap gap-2 mb-3">
              {form.amenities.map(a => (
                <span key={a} className="flex items-center gap-1.5 bg-white/[0.06] text-white/60 text-xs px-3 py-1.5 rounded-full">
                  {a} <button type="button" onClick={() => removeAmenity(a)} className="text-white/30 hover:text-red-400"><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <Input value={newAmenity} onChange={e => setNewAmenity(e.target.value)} placeholder="Add amenity (e.g. WiFi, Pool)" className={`${inputCls} max-w-xs`}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAmenity(); } }} data-testid="listing-amenity-input" />
              <Button type="button" onClick={addAmenity} variant="outline" className="border-white/[0.10] text-white/40 rounded-xl h-11 px-3 hover:bg-white/[0.05]"><Plus className="w-4 h-4" /></Button>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-[#1c1c1c] border border-white/[0.06] rounded-xl p-6 space-y-5">
            <h2 className="font-heading text-base font-semibold text-white">Badges & Settings</h2>
            <div>
              <label className={labelCls}>Badges</label>
              <div className="flex flex-wrap gap-2">
                {BADGE_OPTIONS.map(b => (
                  <button key={b} type="button" onClick={() => toggleBadge(b)}
                    className={`px-3 py-1.5 text-xs rounded-full font-medium transition-colors ${form.badges.includes(b) ? 'bg-gold text-white' : 'bg-white/[0.06] text-white/40 hover:bg-white/[0.10]'}`}
                    data-testid={`badge-${b.toLowerCase()}`}>{b}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="flex items-center justify-between"><label className="text-sm text-white/50">Visible</label><Switch checked={form.is_visible} onCheckedChange={v => set('is_visible', v)} data-testid="listing-visible" /></div>
              <div><label className={labelCls}>Display Order</label><Input type="number" value={form.display_order} onChange={e => set('display_order', parseInt(e.target.value) || 0)} className={`${inputCls} w-24`} /></div>
            </div>
          </motion.div>

          <div className="flex items-center gap-4">
            <Button type="submit" disabled={saving} className="bg-gold hover:bg-[#9A7B1F] text-white h-12 px-8 text-xs uppercase tracking-[0.2em] font-semibold rounded-full gap-2" data-testid="save-listing">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {isEdit ? 'Update Listing' : 'Create Listing'}
            </Button>
            <Link to="/admin/listings"><Button type="button" variant="outline" className="border-white/[0.10] text-white/40 hover:text-white rounded-full h-12 px-6 text-xs uppercase tracking-widest">Cancel</Button></Link>
          </div>
        </form>
      </div>
    </div>
  );
}
